import React from "react";
import { format, addDays, startOfWeek, isSameDay, compareAsc } from "date-fns";
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import DayView from "./DayView";
import { CalendarProps } from "../../types/calendar-props";
import PostCard from "./PostCard";

interface FourWeekViewProps extends CalendarProps {
    currentDate: Date;
}

const FourWeekView: React.FC<FourWeekViewProps> = ({
    posts,
    currentDate,
    loading,
    handleMoreClick,
    closeDayView,
    selectedDay,
}) => {
    // Calculate the start of the current week (Monday)
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const totalDays = 28;
    // Generate array of dates for 4 weeks (28 days)
    const daysArray = Array.from({ length: totalDays }, (_, i) =>
        addDays(weekStart, i)
    );

    // Split days into weeks (an array of arrays)
    const weeks = [];
    for (let i = 0; i < totalDays; i += 7) {
        weeks.push(daysArray.slice(i, i + 7));
    }

    const renderDayCell = (day: Date) => {
        // Filter and sort posts for the given day
        const dayPosts = posts
            ?.filter((post) => isSameDay(new Date(post.scheduledFor), day))
            ?.sort((a, b) =>
                compareAsc(new Date(a.scheduledFor), new Date(b.scheduledFor))
            );

        // Only show up to 2 posts per day in this view
        const postsToShow = dayPosts?.slice(0, 2);
        const hasMorePosts = dayPosts?.length > 2;
        const isToday = isSameDay(day, new Date());

        return (
            <div
                key={day.toString()}
                className="p-1 border border-gray-200 relative"
                style={{
                    backgroundColor: isToday
                        ? "rgba(240, 223, 175, 0.50)"
                        : "transparent",
                    // Each cell gets a minimum height based on viewport height, minus header space
                    minHeight: (window.innerHeight - 225) / 4,
                }}
            >
                <div className="absolute top-2 left-2 text-xs font-semibold">
                    {format(day, "d")}
                </div>
                <div className="mt-6 space-y-1">
                    {postsToShow?.map((post, index) => (
                        <PostCard
                            date={format(day, "EEE-d")}
                            idx={index}
                            key={post.id}
                            post={post}
                        />
                    ))}
                    {hasMorePosts && (
                        <div
                            className="flex justify-center items-center cursor-pointer mt-2"
                            onClick={() => handleMoreClick(day)}
                        >
                            <HiOutlineDotsHorizontal
                                size={20}
                                className="text-primary"
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Days of week for the header row – always 7 columns
    const daysOfWeek = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
    ];

    return (
        <>
            {/* Wrap in an overflow container so horizontal scrolling is available on smaller screens */}
            <div className="overflow-x-auto">
                {/* The min-w ensures that seven columns always render at a minimum width */}
                <div className="w-full min-w-[700px]">
                    {/* Header row */}
                    <div className="grid grid-cols-7">
                        {daysOfWeek.map((day) => (
                            <div
                                key={day}
                                className="p-1 border border-gray-200 border-b-secondary text-center font-semibold"
                            >
                                {day}
                            </div>
                        ))}
                    </div>
                    {/* Calendar weeks – each week forms a grid row with 7 columns */}
                    {weeks.map((week, index) => (
                        <div key={index} className="grid grid-cols-7">
                            {week.map(renderDayCell)}
                        </div>
                    ))}
                </div>
            </div>
            {/* Render the day view overlay if a day is selected */}
            {selectedDay && (
                <DayView
                    day={selectedDay}
                    posts={posts}
                    onClose={closeDayView}
                    loading={loading}
                />
            )}
        </>
    );
};

export default FourWeekView;
